<script setup>
import { onMounted, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';

import { useAdminStore } from '@/store/AdminStore';
import SensorTypesManager from '@/components/Admin/SensorTypesManager.vue';
import UsersManager from '@/components/Admin/UsersManager.vue';
import SystemSettings from '@/components/Admin/SystemSettings.vue';
import AdminStats from '@/components/Admin/AdminStats.vue';

const adminStore = useAdminStore();
const toast = useToast();
const activeTab = ref(0);

onMounted(async () => {
  try {
    await Promise.all([
      adminStore.fetchSystemStats(),
      adminStore.fetchSensorTypes(),
      adminStore.fetchUsers(),
    ]);
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load admin data',
      life: 3000,
    });
  }
});
</script>

<template>
  <div class="py-4 container-fluid admin-dashboard">
    <Toast />

    <!-- Header with animated gradient -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="admin-header">
          <div class="header-bg"></div>
          <div class="header-content">
            <div class="header-icon">
              <i class="fas fa-terminal"></i>
            </div>
            <div class="header-text">
              <h2 class="header-title">Admin Control Panel</h2>
              <p class="header-subtitle">System management • User control • Configuration</p>
            </div>
            <div class="header-badge">
              <span class="status-dot"></span>
              System Online
            </div>
          </div>
        </div>
      </div>
    </div>

    <AdminStats />

    <div class="row">
      <div class="col-12">
        <div class="card admin-card">
          <div class="card-body p-0">
            <TabView v-model:activeIndex="activeTab" class="admin-tabs">
              <TabPanel>
                <template #header>
                  <div class="tab-header">
                    <i class="fas fa-microchip"></i>
                    <span>Sensor Types</span>
                  </div>
                </template>
                <div class="tab-content">
                  <SensorTypesManager />
                </div>
              </TabPanel>

              <TabPanel>
                <template #header>
                  <div class="tab-header">
                    <i class="fas fa-users-cog"></i>
                    <span>Users</span>
                  </div>
                </template>
                <div class="tab-content">
                  <UsersManager />
                </div>
              </TabPanel>

              <TabPanel>
                <template #header>
                  <div class="tab-header">
                    <i class="fas fa-sliders-h"></i>
                    <span>System</span>
                  </div>
                </template>
                <div class="tab-content">
                  <SystemSettings />
                </div>
              </TabPanel>
            </TabView>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.admin-dashboard {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Admin Header */
.admin-header {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  padding: 2rem;
}

.header-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 20px;
}

.header-bg::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.5), transparent);
}

.header-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.header-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 30px rgba(0, 212, 255, 0.3);
  animation: iconFloat 3s ease-in-out infinite;
}

@keyframes iconFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

.header-icon i {
  font-size: 1.75rem;
  color: white;
}

.header-text {
  flex: 1;
}

.header-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  background: linear-gradient(135deg, #f0f0f5 0%, #00d4ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-subtitle {
  margin: 0;
  color: #6b6b7b;
  font-size: 0.9rem;
  letter-spacing: 1px;
}

.header-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 30px;
  color: #10b981;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-dot {
  width: 8px;
  height: 8px;
  background: #10b981;
  border-radius: 50%;
  animation: statusPulse 2s ease-in-out infinite;
}

@keyframes statusPulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
  50% { opacity: 0.8; box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
}

/* Admin Card */
.admin-card {
  border-radius: 20px !important;
  overflow: hidden;
}

.tab-content {
  padding: 1.5rem;
}

/* Tab Styling */
.tab-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
}

.tab-header i {
  font-size: 1.1rem;
}

:deep(.admin-tabs .p-tabview-nav) {
  background: rgba(20, 20, 30, 0.5);
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.75rem 1rem 0;
  gap: 0.5rem;
}

:deep(.admin-tabs .p-tabview-nav li) {
  margin: 0;
}

:deep(.admin-tabs .p-tabview-nav li .p-tabview-nav-link) {
  background: transparent;
  border: none;
  border-radius: 12px 12px 0 0;
  padding: 1rem 1.5rem;
  color: #6b6b7b;
  transition: all 0.3s ease;
  position: relative;
}

:deep(.admin-tabs .p-tabview-nav li .p-tabview-nav-link::after) {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 50%;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #00d4ff, #7c3aed);
  transition: all 0.3s ease;
  transform: translateX(-50%);
  box-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

:deep(.admin-tabs .p-tabview-nav li .p-tabview-nav-link:not(.p-disabled):focus) {
  box-shadow: none;
}

:deep(.admin-tabs .p-tabview-nav li:hover .p-tabview-nav-link:not(.p-disabled)) {
  background: rgba(0, 212, 255, 0.05);
  color: #a0a0b0;
}

:deep(.admin-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link) {
  background: rgba(0, 212, 255, 0.1);
  color: #00d4ff;
}

:deep(.admin-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link::after) {
  width: 100%;
}

:deep(.admin-tabs .p-tabview-panels) {
  background: transparent;
  padding: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .admin-header {
    padding: 1.5rem;
  }
  
  .header-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }
  
  .header-icon i {
    font-size: 1.25rem;
  }
  
  .header-title {
    font-size: 1.35rem;
  }
  
  .header-badge {
    width: 100%;
    justify-content: center;
    margin-top: 0.5rem;
  }
  
  :deep(.admin-tabs .p-tabview-nav) {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 0.5rem 0.5rem 0;
  }
  
  :deep(.admin-tabs .p-tabview-nav li .p-tabview-nav-link) {
    padding: 0.75rem 1rem;
    white-space: nowrap;
  }
  
  .tab-header span {
    display: none;
  }
}
</style>
